import React, { useState } from 'react';
import { Calendar, MapPin, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '../../../components/dialog';
import { Input } from '../../../components/input';
import { Label } from '../../../components';
import CivicPointsLogo from '../../../assets/icons/civicpoints_logo.svg';
import type { PublicEventItem } from '../types/event.types';

interface FormData {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
}

interface WebRegistrationFormModalProps {
    open: boolean;
    onClose: () => void;
    event: PublicEventItem | null;
    formData: FormData;
    onFormDataChange: (data: FormData) => void;
    onRegister: () => void;
    registering: boolean;
    formatFullDateTime: (dateString: string, timeString: string, timeZone?: string | null) => string;
}

const WebRegistrationFormModal: React.FC<WebRegistrationFormModalProps> = ({
    open,
    onClose,
    event,
    formData,
    onFormDataChange,
    onRegister,
    registering,
    formatFullDateTime
}) => {
    const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

    const handleInputChange = (field: keyof FormData, value: string) => {
        onFormDataChange({
            ...formData,
            [field]: value
        });
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<Record<keyof FormData, string>> = {};
        
        if (!formData.firstName.trim()) {
            newErrors.firstName = 'Please fill out this field.';
        }
        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Please fill out this field.';
        }
        if (!formData.email.trim()) {
            newErrors.email = 'Please fill out this field.';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (validateForm()) {
            onRegister();
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent 
                className="fixed inset-0 top-0 bottom-0 left-0 right-0 translate-y-0 translate-x-0 max-w-full w-full h-full rounded-none p-0 m-0 [&>button]:hidden"
                dialogOverlayClassName="bg-black/50"
                style={{ zIndex: 100 }}
            >
                <DialogTitle className="sr-only">Register for Event</DialogTitle>
                <div className="flex flex-col h-full">
                    {/* Scrollable Content */}
                    <div className="flex-1 overflow-y-auto">
                        <div className="p-6 pb-24">
                            {/* Header with Logo and Close Button */}
                            <div className="flex items-center justify-between mb-8">
                                <img src={CivicPointsLogo} alt="CivicPoints" className="h-5" />
                                <button
                                    onClick={onClose}
                                    className="text-gray-400 hover:text-gray-600 z-10 relative"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Register for Event Title */}
                            <h2 className="text-xl font-bold text-gray-900 mb-4">Register for Event</h2>

                            {/* Event Details */}
                            <div className="mb-6 space-y-3">
                                <h3 className="text-lg font-semibold text-gray-900">{event?.title}</h3>
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <Calendar className="w-4 h-4" />
                                    <span>{event && formatFullDateTime(event.event_date, event.start_time, event.time_zone)}</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <MapPin className="w-4 h-4" />
                                    <span>{event?.location || 'Location TBD'}</span>
                                </div>
                            </div>

                            {/* Registration Form */}
                            <div className="space-y-4">
                                {/* First Name */}
                                <div>
                                    <Label className="text-sm font-medium text-gray-700 mb-2 block">
                                        First Name
                                    </Label>
                                    <Input
                                        type="text"
                                        placeholder="Enter your first name"
                                        value={formData.firstName}
                                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                                        className={`w-full ${errors.firstName ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                                    />
                                    {errors.firstName && (
                                        <p role="alert" className="mt-1 text-sm text-red-600">
                                            {errors.firstName}
                                        </p>
                                    )}
                                </div>

                                {/* Last Name */}
                                <div>
                                    <Label className="text-sm font-medium text-gray-700 mb-2 block">
                                        Last Name
                                    </Label>
                                    <Input
                                        type="text"
                                        placeholder="Enter your last name"
                                        value={formData.lastName}
                                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                                        className={`w-full ${errors.lastName ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                                    />
                                    {errors.lastName && (
                                        <p role="alert" className="mt-1 text-sm text-red-600">
                                            {errors.lastName}
                                        </p>
                                    )}
                                </div>

                                {/* Email */}
                                <div>
                                    <Label className="text-sm font-medium text-gray-700 mb-2 block">
                                        Email
                                    </Label>
                                    <Input
                                        type="email"
                                        placeholder="your.email@example.com"
                                        value={formData.email}
                                        onChange={(e) => handleInputChange('email', e.target.value)}
                                        className={`w-full ${errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                                    />
                                    {errors.email && (
                                        <p role="alert" className="mt-1 text-sm text-red-600">
                                            {errors.email}
                                        </p>
                                    )}
                                </div>

                                {/* Phone */}
                                {/* <div>
                                    <Label className="text-sm font-medium text-gray-700 mb-2 block">
                                        Phone
                                    </Label>
                                    <Input
                                        type="tel"
                                        placeholder="+84901234567"
                                        value={formData.phone}
                                        onChange={(e) => handleInputChange('phone', e.target.value)}
                                        className="w-full"
                                    />
                                </div> */}
                            </div>
                        </div>
                    </div>

                    {/* Fixed Footer with Register Button and Disclaimer */}
                    <div className="fixed bottom-0 left-0 right-0 bg-white p-6 z-10">
                        <button
                            onClick={handleSubmit}
                            disabled={registering}
                            className="w-full h-[44px] rounded-[33px] text-black font-semibold text-base shadow-lg hover:opacity-90 transition-opacity mb-3 disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{ backgroundColor: 'hsla(167, 100%, 42%, 1)' }}
                        >
                            Register
                        </button>
                        <p className="text-xs text-gray-500 text-center">
                            By registering, you agree to receive event updates via email.
                        </p>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default WebRegistrationFormModal;

